import React, { useState } from "react";
import styled from "styled-components/native";
import { Button } from "@components/atoms";
import { LayoutChangeEvent, ScrollView, View } from "react-native";
import ProductDetailList from "./Utils/ProductDetailList/ProductDetailList";
import { ButtonType } from "@components/atoms/Button/types";
import LayoutContainer from "@components/layout/LayoutContainer";
import GalleryHeaderComponentView from "./Utils/GalleryHeaderView/GalleryHeaderComponentView";
import RenderHeaderComponentView from "./Utils/RenderHeaderView/RenderHeaderComponentView";

import { SpaceType } from "@components/atoms/Spacer/types";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import WebPageHeader from "@components/molecules/WebPageHeader/WebPageHeader";
import { css } from "styled-components/native";
import { useFindMatch } from "./FindMatchContext";
import { getButtonCategory } from "@components/molecules/Share/utils/utils";
import LineViewContainer from "@scenes/requests/utils/LineView";
import Loader from "@components/atoms/Loader";
import Breadcrumbs from "@components/molecules/Breadcrumbs/Breadcrumbs";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import Routes from "@navigations/Routes";
import { navigate, push } from "@navigations/utils/RootNavigation";
import { ProductCategory } from "@utils/constants";
import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  setBuilderProduct,
  setDisableJewelrySettingStoneLinkage,
} from "@redux/slices/appStateSlice";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigationHistory } from "../../hooks/useNavigationHistory";
import { useJewelryConfiguratorState } from "@scenes/configurator/JewelryConfigurator/Context/JewelryConfiguratorContext";
import {
  BreadCrumbLabel,
  NavigationHistoryState,
} from "@components/molecules/Breadcrumbs/utils";
import { normalizeBoolean } from "@components/atoms/Filters/ButtonSection/Utils";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";

const FindMatchContent = () => {
  const {
    t,
    theme,
    itemDetail,
    otherDetail,
    currentIndex,
    productType,
    handleItemClick,
    loadingData,
    enterSecondFlow,
    showPairButton,
  } = useFindMatch();
  const mEnterSecondFlow = normalizeBoolean(enterSecondFlow);
  const matchingPairItem = otherDetail?.[currentIndex] || {};
  const { getPriceMode } = useUserInfo();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { addRouteToHistory } = useNavigationHistory();
  const { history } = useSelector(
    (state: any) => state.navigationHistory as NavigationHistoryState
  );
  const insets = useSafeAreaInsets();

  const {
    setStoneItemDetail,
    setItemDetail,
    setMatchingPairItemDetail,
    stoneItemDetail,
    itemDetail: mItemDetail,
  } = useJewelryConfiguratorState();

  const jewelryStyle =
    mEnterSecondFlow === true
      ? stoneItemDetail?.jewelry_style
      : mItemDetail?.jewelry_style;

  const isEarring = jewelryStyle?.includes("Earring");

  const goToJewelry = () => {
    const name =
      theme.isLargeScreen || theme.isTablet
        ? Routes.FilterSplitScreen
        : Routes.SearchResultScreen;
    const params = {
      priceMode: getPriceMode(),
      productType: ProductCategory.JEWELRY,
      fromNewFilterScreen: false,
      filterSplitStep: 2,
      sectionName: SearchFilterTypeEnum.JEWELRY,
      productPrefix: ProductCategory.JEWELRY,
      breadCrumbLabel: "Setting Search",
      enterSecondFlow: true,
      saved_search: JSON.stringify({
        jewelry_styles: ["earring"],
        itemType: 2,
        sub_type: null,
        preference: [],
        vdb_setting: "true",
        stock_item_ids: [itemDetail.id as number],
        is_center_size_selected: "true",
      }),
      searchForRing: false,
      searchForPendant: false,
    };
    dispatch(setDisableJewelrySettingStoneLinkage(false));
    dispatch(setBuilderProduct(ProductCategory.JEWELRY));
    const mRoute = { name, params };
    addRouteToHistory(mRoute);
    setStoneItemDetail(itemDetail);
    setMatchingPairItemDetail(matchingPairItem);
    queryClient.resetQueries({ queryKey: ["PRODUCT_PAGE"] });
    setItemDetail({});
    push(name, params);
  };

  const goToReview = () => {
    const params = {
      breadCrumbLabel: BreadCrumbLabel.REVIEW,
      enterSecondFlow: mEnterSecondFlow,
      priceMode: getPriceMode(),
      productPrefix: ProductCategory.JEWELRY,
      productType: ProductCategory.JEWELRY,
    };
    const mRoute = { name: Routes.ReviewPage, params };
    setMatchingPairItemDetail(matchingPairItem);
    setItemDetail(itemDetail);
    addRouteToHistory(mRoute);
    navigate(Routes.ReviewPage, params);
  };

  const { enableJewelryBuilder } = useOrgSettingsSlice();
  const { deviceWidth: containerWidth } = useIsFoldable();
  const paddingContainerWidth = theme.isLargeScreen
    ? Math.min(1176, containerWidth)
    : theme.isTablet
    ? Math.min(900, containerWidth)
    : null;

  const [contentWidth, setContentWidth] = useState(paddingContainerWidth);
  const handleLayout = (e: LayoutChangeEvent) => {
    setContentWidth(e.nativeEvent.layout.width);
  };
  const imageWidth =
    (contentWidth ?? containerWidth) / 2 -
    (theme.isLargeScreen ? 175 : theme.isMobile ? 30 : 100);

  const showChoosePairButton =
    enableJewelryBuilder &&
    (mEnterSecondFlow === true ? isEarring : showPairButton);

  const showBreadCrumbInMobile =
    enableJewelryBuilder && history.length >= 2 && theme.isMobile;

  if (loadingData) return <Loader />;

  return (
    <MainContainer>
      {showBreadCrumbInMobile && (
        <BreadCrumbContainerMobile>
          <Breadcrumbs />
        </BreadCrumbContainerMobile>
      )}
      <ScrollView style={{ ...(showBreadCrumbInMobile && { marginTop: 50 }) }}>
        {theme.isLargeScreen && !enableJewelryBuilder ? (
          <WebPageHeader
            maxWidth={paddingContainerWidth - 50 ?? containerWidth}
            isV2={true}
          />
        ) : null}

        <CenterContainer maxWidth={paddingContainerWidth ?? containerWidth}>
          {enableJewelryBuilder && theme.isTablet && (
            <View
              style={{
                backgroundColor: theme["core-01"],
                borderBottomColor: theme["core-03"],
                borderBottomWidth: 1,
              }}
            >
              <Breadcrumbs />
            </View>
          )}
          {enableJewelryBuilder && theme.isLargeScreen && <Breadcrumbs />}
          <PadingContainer onLayout={handleLayout}>
            <RenderHeaderComponentView />
            <GalleryHeaderComponentView containerWidth={imageWidth} />
            <ProductDetailList />
          </PadingContainer>
        </CenterContainer>
      </ScrollView>
      <LineViewContainer background="core-03" />
      <ButtonView
        paddingBottom={insets.bottom > 0 ? insets.bottom : SpaceType.Double}
      >
        <CenterContainer maxWidth={paddingContainerWidth ?? containerWidth}>
          {!showChoosePairButton && (
            <ButtonContainer
              paddingInline={
                (paddingContainerWidth ?? containerWidth) < containerWidth
                  ? 36
                  : theme.isMobile
                  ? 0
                  : 20
              }
            >
              <ButtonFlexContainer>
                <Button
                  type={ButtonType.secondary}
                  category={getButtonCategory(productType)}
                  testID="findMatch.content.button.leftDetail"
                  title={t("findMatch.button.left.detail.text")}
                  onPress={() => handleItemClick(itemDetail)}
                  maxWidth={theme.isLargeScreen ? 512 : undefined}
                />
              </ButtonFlexContainer>
              <GapView />
              <ButtonFlexContainer>
                <Button
                  type={ButtonType.secondary}
                  category={getButtonCategory(productType)}
                  testID="findMatch.content.button.rightDetail"
                  title={t("findMatch.button.right.detail.text")}
                  onPress={() => handleItemClick(otherDetail?.[currentIndex])}
                  maxWidth={theme.isLargeScreen ? 512 : undefined}
                />
              </ButtonFlexContainer>
            </ButtonContainer>
          )}
          {showChoosePairButton && (
            <ButtonAddToJewelryContainer
              paddingInline={
                (paddingContainerWidth ?? containerWidth) < containerWidth
                  ? 36
                  : theme.isMobile
                  ? 0
                  : 20
              }
            >
              <Button
                type={ButtonType.primary}
                category={getButtonCategory(productType)}
                testID="findMatch.content.button.addToJewelry"
                title={t("productDetailPage.addToEarring.title.text")}
                onPress={() =>
                  mEnterSecondFlow === true ? goToReview() : goToJewelry()
                }
                maxWidth={theme.isMobile ? containerWidth : 139}
                disabled={!(matchingPairItem?.id > 0 && itemDetail?.id > 0)}
              />
            </ButtonAddToJewelryContainer>
          )}
        </CenterContainer>
      </ButtonView>
    </MainContainer>
  );
};

const MainContainer = styled(LayoutContainer)`
  flex: 1;
  background-color: ${({ theme }) => theme["core-02"]};
`;

const PadingContainer = styled.View`
  padding-left: 16px;
  padding-right: 16px;
  flex: 1;
  flex-basis: auto;
`;

const CenterContainer = styled.View<{ maxWidth?: number }>`
  align-self: center;
  max-width: ${({ maxWidth }) => (maxWidth ? `${maxWidth}px` : "100%")};
  width: 100%;
`;

const ButtonView = styled.View<{
  paddingBottom: number;
}>`
  background-color: ${({ theme }) => theme["core-01"]};
  padding-horizontal: ${SpaceType.Double}px;
  padding-top: ${({ theme }) =>
    theme.isMobile ? SpaceType.Basic : SpaceType.Double}px;
  padding-bottom: ${({ paddingBottom }: { paddingBottom: number }) =>
    paddingBottom}px;
  align-items: center;
  justify-content: center;
`;

const ButtonContainer = styled.View<{ paddingInline: number }>`
  flex-direction: row;
  width: 100%;
  justify-content: center;
  padding-inline: ${({ paddingInline }) => paddingInline}px;
`;

const ButtonFlexContainer = styled.View`
  padding-left: 4px;
  padding-right: 4px;
  flex: 1;
  margin-left: ${({ theme }) => (theme.isTablet ? SpaceType.Big : 0)};
  margin-right: ${({ theme }) => (theme.isTablet ? SpaceType.Big : 0)};
  flex-basis: auto;
`;

const ButtonAddToJewelryContainer = styled.View<{ paddingInline: number }>`
  flex-direction: row;
  width: 100%;
  justify-content: ${({ theme }) => (theme.isMobile ? "center" : "flex-end")};
  align-items: center;
  padding-inline: ${({ paddingInline }) => paddingInline}px;
`;

const BreadCrumbContainerMobile = styled.View`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  width: 100%;
`;

const GapView = styled.View`
  ${({ theme }) =>
    theme.isLargeScreen &&
    css`
      width: 68px;
    `}
`;

export default FindMatchContent;
